/**
 * System Performance Metrics Page
 *
 * Displays comprehensive system performance metrics:
 * - Cache performance (Redis stats)
 * - Database metrics (size, tables, connections)
 * - Query performance
 * - Cron job status
 */

import React, { useState, useEffect } from 'react';
import {
  Activity, Database, Server, Clock, CheckCircle, AlertCircle,
  TrendingUp, HardDrive, Zap, RefreshCw
} from 'lucide-react';
import api from '../services/api';
import { Navbar } from '../components/shared/Navbar';

interface CacheStats {
  connected: boolean;
  keys?: number;
  memory?: string;
  hitRate?: number;
  hits?: number;
  misses?: number;
  totalCommands?: number;
  connections?: number;
  evictions?: number;
  error?: string;
}

interface DatabaseStats {
  sizeBytes: number;
  sizeMB: number;
  sizeGB: string;
  tables: Array<{
    name: string;
    rows: number;
    sizeMB: number;
  }>;
  connections: {
    total: number;
    active: number;
    idle: number;
  };
}

interface PerformanceStats {
  avgQueryTime: number | null;
  slowestQuery: number | null;
  totalQueries: number | null;
  note: string | null;
}

interface CronJob {
  name: string;
  lastRun: string | null;
  records24h: number;
  status: 'healthy' | 'stale';
}

interface SystemMetrics {
  cache: CacheStats;
  database: DatabaseStats;
  performance: PerformanceStats;
  cronJobs: CronJob[];
  generatedAt: string;
}

export const SystemMetricsPage: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/system-metrics');
      setMetrics(response.data.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load system metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchMetrics, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
    return undefined;
  }, [autoRefresh]);

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading system metrics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 font-semibold mb-2">Failed to load metrics</p>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchMetrics}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Activity className="text-blue-600" />
              System Performance Metrics
            </h1>
            <p className="text-gray-600 mt-2">
              Real-time monitoring of cache, database, and cron job performance
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300"
              />
              Auto-refresh (30s)
            </label>
            <button
              onClick={fetchMetrics}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Cache Stats */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="text-yellow-500" />
            Redis Cache Performance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Connection Status"
              value={metrics.cache.connected ? 'Connected' : 'Disconnected'}
              icon={<Server />}
              color={metrics.cache.connected ? 'green' : 'red'}
            />
            {metrics.cache.connected && (
              <>
                <StatCard
                  title="Cache Hit Rate"
                  value={`${metrics.cache.hitRate?.toFixed(2)}%`}
                  subtitle={`${metrics.cache.hits?.toLocaleString()} hits, ${metrics.cache.misses?.toLocaleString()} misses`}
                  icon={<TrendingUp />}
                  color={metrics.cache.hitRate && metrics.cache.hitRate > 80 ? 'green' : metrics.cache.hitRate && metrics.cache.hitRate > 50 ? 'yellow' : 'red'}
                />
                <StatCard
                  title="Cached Keys"
                  value={metrics.cache.keys?.toLocaleString() || '0'}
                  subtitle={`Memory: ${metrics.cache.memory}`}
                  icon={<Database />}
                  color="blue"
                />
                <StatCard
                  title="Total Commands"
                  value={metrics.cache.totalCommands?.toLocaleString() || '0'}
                  subtitle={`${metrics.cache.evictions || 0} evictions`}
                  icon={<Activity />}
                  color="purple"
                />
              </>
            )}
            {!metrics.cache.connected && metrics.cache.error && (
              <div className="col-span-3 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{metrics.cache.error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Database Stats */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HardDrive className="text-blue-500" />
            Database Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard
              title="Database Size"
              value={`${metrics.database.sizeGB} GB`}
              subtitle={`${metrics.database.sizeMB.toLocaleString()} MB`}
              icon={<HardDrive />}
              color="blue"
            />
            <StatCard
              title="Total Tables"
              value={metrics.database.tables.length.toString()}
              subtitle="Top 20 by row count"
              icon={<Database />}
              color="indigo"
            />
            <StatCard
              title="Active Connections"
              value={metrics.database.connections.active.toString()}
              subtitle={`${metrics.database.connections.total} total, ${metrics.database.connections.idle} idle`}
              icon={<Activity />}
              color="green"
            />
            <StatCard
              title="Query Performance"
              value={metrics.performance.avgQueryTime ? `${metrics.performance.avgQueryTime.toFixed(2)}ms` : 'N/A'}
              subtitle={metrics.performance.note || `${metrics.performance.totalQueries?.toLocaleString()} queries`}
              icon={<Zap />}
              color={metrics.performance.avgQueryTime && metrics.performance.avgQueryTime < 50 ? 'green' : 'yellow'}
            />
          </div>

          {/* Top Tables */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Top Tables by Row Count</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-4 text-sm font-semibold text-gray-700">Table Name</th>
                    <th className="text-right py-2 px-4 text-sm font-semibold text-gray-700">Rows</th>
                    <th className="text-right py-2 px-4 text-sm font-semibold text-gray-700">Size (MB)</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.database.tables.slice(0, 10).map((table, idx) => (
                    <tr key={table.name} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                      <td className="py-2 px-4 text-sm text-gray-900 font-mono">{table.name}</td>
                      <td className="py-2 px-4 text-sm text-gray-900 text-right">{table.rows.toLocaleString()}</td>
                      <td className="py-2 px-4 text-sm text-gray-900 text-right">{table.sizeMB.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Cron Jobs Status */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="text-purple-500" />
            Automated Jobs Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.cronJobs.map((job) => (
              <div key={job.name} className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm">{job.name.replace(/-/g, ' ').toUpperCase()}</h3>
                  {job.status === 'healthy' ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <div className="text-sm text-gray-600 mb-1">
                  <strong>Last Run:</strong> {job.lastRun ? new Date(job.lastRun).toLocaleString() : 'Never'}
                </div>
                <div className="text-sm text-gray-600">
                  <strong>24h Records:</strong> {job.records24h.toLocaleString()}
                </div>
                <div className="mt-2">
                  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    job.status === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {job.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 mt-8">
          Last updated: {new Date(metrics.generatedAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'green' | 'yellow' | 'red' | 'blue' | 'indigo' | 'purple';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color }) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    red: 'bg-red-100 text-red-700',
    blue: 'bg-blue-100 text-blue-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    purple: 'bg-purple-100 text-purple-700'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-600">{title}</h3>
        <div className={`p-2 rounded ${colorClasses[color]}`}>
          {React.cloneElement(icon as React.ReactElement, { className: 'h-5 w-5' } as any)}
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
    </div>
  );
};

export default SystemMetricsPage;
